/**
 * Safe fetch utility that prevents "Unexpected token '<', '<!doctype '... is not valid JSON" errors
 * when endpoints or gateways return HTML error pages (e.g. 502, 503, 404 or dev server fallback).
 */
export async function safeFetchJson<T = any>(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<{ ok: boolean; status: number; data: T | null; error?: string }> {
  try {
    const headers = new Headers(init?.headers || {});
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json, text/plain, */*');
    }

    const response = await fetch(input, {
      ...init,
      headers
    });

    const status = response.status;
    const contentType = response.headers.get('content-type') || '';
    const rawText = await response.text();

    // Check if the response is an HTML document or error page
    const trimmed = rawText.trim();
    if (
      trimmed.startsWith('<!DOCTYPE') ||
      trimmed.startsWith('<!doctype') ||
      trimmed.startsWith('<html') ||
      trimmed.startsWith('<?xml') ||
      contentType.includes('text/html')
    ) {
      console.warn(`[safeFetchJson] Received HTML response instead of JSON from ${String(input)} (Status: ${status})`);
      return {
        ok: false,
        status,
        data: null,
        error: `Servidor retornou resposta HTML (Status: ${status})`
      };
    }

    if (!trimmed) {
      return {
        ok: response.ok,
        status,
        data: null
      };
    }

    try {
      const parsedData = JSON.parse(rawText) as T;
      return {
        ok: response.ok,
        status,
        data: parsedData,
        error: response.ok ? undefined : ((parsedData as any)?.error || (parsedData as any)?.message || `Erro HTTP ${status}`)
      };
    } catch (parseErr: any) {
      console.warn(`[safeFetchJson] Failed to parse JSON from ${String(input)}:`, parseErr.message);
      return {
        ok: false,
        status,
        data: null,
        error: `Resposta não é um JSON válido (Status: ${status})`
      };
    }
  } catch (netErr: any) {
    console.warn(`[safeFetchJson] Network error for ${String(input)}:`, netErr.message);
    return {
      ok: false,
      status: 0,
      data: null,
      error: netErr.message || 'Erro de conexão na requisição'
    };
  }
}
